import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useResponsiveDesign } from '../hooks/useResponsiveDesign';

interface LayoutContextType {
  isMobile: boolean;
  isSmallScreen: boolean; // Added for tablet support (< lg)
  isSidebarOpen: boolean;
  isSidebarCollapsed: boolean;
  toggleSidebar: () => void;
  closeSidebar: () => void;
}

const LayoutContext = createContext<LayoutContextType | undefined>(undefined);

export const LayoutProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isMobile, isSmallScreen } = useResponsiveDesign();

  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(false); // Mobile/Tablet drawer state
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState<boolean>(() => {
    // Desktop only - persist preference
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved ? JSON.parse(saved) : false;
  });

  // Update localStorage when collapsed state changes
  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', JSON.stringify(isSidebarCollapsed));
  }, [isSidebarCollapsed]);

  const toggleSidebar = useCallback(() => {
    if (isSmallScreen) {
      setIsSidebarOpen((prev) => !prev);
    } else {
      setIsSidebarCollapsed((prev) => !prev);
    }
  }, [isSmallScreen]);

  const closeSidebar = useCallback(() => {
    if (isSmallScreen) {
      setIsSidebarOpen(false);
    }
  }, [isSmallScreen]);

  return (
    <LayoutContext.Provider
      value={{
        isMobile,
        isSmallScreen,
        isSidebarOpen,
        isSidebarCollapsed,
        toggleSidebar,
        closeSidebar,
      }}
    >
      {children}
    </LayoutContext.Provider>
  );
};

export const useLayout = (): LayoutContextType => {
  const context = useContext(LayoutContext);
  if (context === undefined) {
    throw new Error('useLayout must be used within a LayoutProvider');
  }
  return context;
};
